import {
  collection,
  addDoc,
  deleteDoc,
  doc,
  getDoc,
  getDocs,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
  writeBatch,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL, deleteObject } from 'firebase/storage';
import { db, storage } from '../firebase';

const COLLECTION = 'templatesImports';
/** Public mirror for /welcome/JTBootcamp (unauthenticated read). No secrets — file metadata + download URL only. */
const BOOTCAMP_COLLECTION = 'bootcampImports';
/** Lead gate submissions from /welcome/JTBootcamp (internal read; public create via rules). */
const UNLOCK_LEADS_COLLECTION = 'jtBootcampUnlockLeads';
/** CRM-only event lines that split the unlock submissions list (one doc per line). */
const UNLOCK_LINES_COLLECTION = 'jtBootcampUnlockLines';
const BOOTCAMP_SETTINGS_COLLECTION = 'jtBootcampSettings';
const BOOTCAMP_SETTINGS_CONFIG_ID = 'config';
/** Shared display settings for the imports (group emojis picked from CRM). */
const IMPORTS_SETTINGS_COLLECTION = 'templatesImportsSettings';
const IMPORTS_SETTINGS_CONFIG_ID = 'config';

export const JT_BOOTCAMP_UNLOCK_SOURCE = 'jt-bootcamp';

function isJsonImportTab(tabId) {
  return tabId === 'dashboards' || tabId === 'workflows';
}

/** Dashboards and Workflows use JSON; other tabs use CSV. */
export function getFileKindForTab(tabId) {
  return isJsonImportTab(tabId) ? 'json' : 'csv';
}

/** Resolve file kind from stored row (new `fileKind`, legacy rows infer from tab / extension). */
export function getFileKindFromRow(row) {
  if (row?.fileKind === 'json' || row?.fileKind === 'csv') return row.fileKind;
  const tab = row?.tabId ?? row?.importType;
  if (tab === 'dashboards' || tab === 'workflows') return 'json';
  const n = (row?.fileName || '').toLowerCase();
  if (n.endsWith('.json')) return 'json';
  return 'csv';
}

function sanitizeFileName(name) {
  return name.replace(/[^a-zA-Z0-9._-]/g, '_');
}

/** Upload metadata forcing browsers to save (not render) the file if its URL is opened directly. */
function attachmentUploadMetadata(fileName) {
  const safe = sanitizeFileName(fileName || 'download');
  return { contentDisposition: `attachment; filename="${safe}"` };
}

function assertImportFile(tabId, fileName) {
  const lower = fileName.toLowerCase();
  if (isJsonImportTab(tabId)) {
    if (!lower.endsWith('.json')) {
      throw new Error(
        tabId === 'dashboards'
          ? 'Dashboard imports must be a .json file.'
          : 'Workflow imports must be a .json file.'
      );
    }
    return;
  }
  if (!lower.endsWith('.csv')) {
    throw new Error('File must be a .csv file.');
  }
}

export const JOBTREAD_TAB_IDS = ['cost-groups', 'schedules', 'dashboards', 'todos', 'workflows'];

export async function uploadTemplatesImportFile({
  tabId,
  title,
  description,
  department,
  file,
  userId,
  authorName,
  authorCompany,
  source = 'crm',
  skipBootcampMirror = false,
}) {
  const fileKind = getFileKindForTab(tabId);

  if (!file) {
    throw new Error(fileKind === 'json' ? 'Choose a JSON file.' : 'Choose a CSV file.');
  }
  assertImportFile(tabId, file.name);

  if (fileKind === 'json') {
    try {
      const text = await file.text();
      JSON.parse(text);
    } catch (e) {
      if (e instanceof SyntaxError) {
        throw new Error('Invalid JSON. Check the file and try again.');
      }
      throw new Error('Could not read the file.');
    }
  }

  if (file.size > 15 * 1024 * 1024) {
    throw new Error('File must be 15MB or smaller.');
  }

  const safeName = sanitizeFileName(file.name);
  const storagePath = `templates-imports/${tabId}/${userId}_${Date.now()}_${safeName}`;
  const storageRef = ref(storage, storagePath);
  const snapshot = await uploadBytes(storageRef, file, attachmentUploadMetadata(file.name));
  const downloadURL = await getDownloadURL(snapshot.ref);

  const titleTrim = title?.trim() || file.name;
  const descriptionTrim = description?.trim() || '';
  const dept = department || 'general';

  const docRef = await addDoc(collection(db, COLLECTION), {
    tabId,
    fileKind,
    title: titleTrim,
    description: descriptionTrim,
    department: dept,
    fileName: file.name,
    storagePath,
    downloadURL,
    size: file.size,
    createdAt: serverTimestamp(),
    createdBy: userId,
    authorName: authorName?.trim() || 'Unknown',
    authorCompany: authorCompany?.trim() || null,
    source,
    version: '1.0',
    emoji: null,
    likes: 0,
    likedBy: [],
    bootcampImportId: null,
  });

  if (!skipBootcampMirror) {
    const bootcampRef = await addDoc(collection(db, BOOTCAMP_COLLECTION), {
      templatesImportId: docRef.id,
      department: dept,
      importType: tabId,
      fileKind,
      title: titleTrim,
      description: descriptionTrim,
      downloadURL,
      storagePath,
      fileName: file.name,
      createdAt: serverTimestamp(),
    });

    await updateDoc(doc(db, COLLECTION, docRef.id), {
      bootcampImportId: bootcampRef.id,
    });
  }

  return { id: docRef.id, downloadURL, storagePath };
}

/** @deprecated Alias — dashboards & workflows use JSON, other tabs CSV */
export const uploadTemplatesImportCsv = uploadTemplatesImportFile;

/** Updates title/description on CRM doc and bootcamp mirror when present. */
export async function updateTemplatesImportMetadata({ docId, title, description }) {
  const titleTrim = (title ?? '').trim();
  const descriptionTrim = (description ?? '').trim();
  if (!titleTrim) {
    throw new Error('Title cannot be empty.');
  }

  const mainRef = doc(db, COLLECTION, docId);
  const snap = await getDoc(mainRef);
  if (!snap.exists) {
    throw new Error('Import not found.');
  }

  const payload = {
    title: titleTrim,
    description: descriptionTrim,
    updatedAt: serverTimestamp(),
  };

  await updateDoc(mainRef, payload);

  const data = snap.data();
  const bootcampId = data?.bootcampImportId;
  if (bootcampId) {
    try {
      await updateDoc(doc(db, BOOTCAMP_COLLECTION, bootcampId), {
        title: titleTrim,
        description: descriptionTrim,
      });
    } catch (e) {
      console.warn('bootcampImports update:', e);
    }
  }
}

/**
 * Add or remove the public `bootcampImports` mirror for an existing import.
 * `published === true` creates the mirror (if absent) and sets `bootcampImportId`;
 * `published === false` deletes the mirror and clears `bootcampImportId`.
 * Returns the resulting `bootcampImportId` (string when published, null when not).
 */
export async function setTemplatesImportBootcampPublished({ docId, published }) {
  const mainRef = doc(db, COLLECTION, docId);
  const snap = await getDoc(mainRef);
  if (!snap.exists()) {
    throw new Error('Import not found.');
  }
  const data = snap.data();
  const existingId = data?.bootcampImportId || null;

  if (published) {
    if (existingId) return existingId;
    const bootcampRef = await addDoc(collection(db, BOOTCAMP_COLLECTION), {
      templatesImportId: docId,
      department: data.department || 'general',
      importType: data.tabId ?? data.importType,
      fileKind: getFileKindFromRow(data),
      title: data.title || data.fileName || 'Untitled',
      description: data.description || '',
      downloadURL: data.downloadURL,
      storagePath: data.storagePath,
      fileName: data.fileName,
      createdAt: serverTimestamp(),
    });
    await updateDoc(mainRef, { bootcampImportId: bootcampRef.id });
    return bootcampRef.id;
  }

  if (existingId) {
    try {
      await deleteDoc(doc(db, BOOTCAMP_COLLECTION, existingId));
    } catch (e) {
      console.warn('bootcampImports delete:', e);
    }
    await updateDoc(mainRef, { bootcampImportId: null });
  }
  return null;
}

function normalizeImportRow(row) {
  return {
    ...row,
    authorName: row.authorName || (row.source === 'crm' ? 'BBA Team' : 'Community'),
    likes: row.likes ?? 0,
    likedBy: row.likedBy ?? [],
    source: row.source || 'crm',
  };
}

export async function listImportsByTab(tabId) {
  const q = query(collection(db, COLLECTION), where('tabId', '==', tabId));
  const snap = await getDocs(q);
  const rows = snap.docs.map((d) => normalizeImportRow({ id: d.id, ...d.data() }));
  // Manual order (sortOrder, written on reorder) wins; rows without one — new
  // uploads and pre-reorder docs — come first, newest first, until placed.
  rows.sort((a, b) => {
    const ao = typeof a.sortOrder === 'number';
    const bo = typeof b.sortOrder === 'number';
    if (ao && bo) return a.sortOrder - b.sortOrder;
    if (ao !== bo) return ao ? 1 : -1;
    const ta = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
    return tb - ta;
  });
  return rows;
}

/** Persist a tab's display order (portal shows the same order). */
export async function saveImportsOrder(orderedIds) {
  const batch = writeBatch(db);
  orderedIds.forEach((id, idx) => {
    batch.update(doc(db, COLLECTION, id), { sortOrder: idx });
  });
  await batch.commit();
}

export async function listAllJobTreadImports() {
  const results = await Promise.all(JOBTREAD_TAB_IDS.map((tabId) => listImportsByTab(tabId)));
  return results.flat();
}

export async function deleteTemplatesImport({ docId, storagePath }) {
  const mainRef = doc(db, COLLECTION, docId);
  const snap = await getDoc(mainRef);
  const data = snap.exists ? snap.data() : null;
  const bootcampId = data?.bootcampImportId;

  if (bootcampId) {
    try {
      await deleteDoc(doc(db, BOOTCAMP_COLLECTION, bootcampId));
    } catch (e) {
      console.warn('bootcampImports delete:', e);
    }
  }

  // Old version files + entries go with the import (best-effort; the current
  // file's path may also appear as a version entry — the second delete warns).
  try {
    const versionsSnap = await getDocs(importVersionsRef(docId));
    await Promise.all(
      versionsSnap.docs.map(async (v) => {
        const vPath = v.data()?.storagePath;
        if (vPath && vPath !== storagePath) {
          try {
            await deleteObject(ref(storage, vPath));
          } catch (e) {
            console.warn('Version storage delete:', e);
          }
        }
        await deleteDoc(v.ref);
      })
    );
  } catch (e) {
    console.warn('Version history delete:', e);
  }

  if (storagePath) {
    try {
      await deleteObject(ref(storage, storagePath));
    } catch (e) {
      console.warn('Storage delete:', e);
    }
  }
  await deleteDoc(mainRef);
}

// ---- Emojis (import cards + group tabs; shown on the portal page too) ----

/** Set or clear ('' / null) the emoji shown on one import's card. */
export async function updateTemplatesImportEmoji({ docId, emoji }) {
  await updateDoc(doc(db, COLLECTION, docId), { emoji: emoji || null });
}

/** Emojis picked for the import groups, keyed by tabId. Missing keys fall back to defaults. */
export async function getImportGroupEmojis() {
  try {
    const snap = await getDoc(doc(db, IMPORTS_SETTINGS_COLLECTION, IMPORTS_SETTINGS_CONFIG_ID));
    return snap.exists() ? snap.data().groupEmojis || {} : {};
  } catch (e) {
    console.warn('Group emojis load:', e);
    return {};
  }
}

/** CRM: save one group's emoji (pass '' to reset to the default). */
export async function saveImportGroupEmoji(tabId, emoji) {
  await setDoc(
    doc(db, IMPORTS_SETTINGS_COLLECTION, IMPORTS_SETTINGS_CONFIG_ID),
    { groupEmojis: { [tabId]: emoji || '' }, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

// ---- Version history (mirrors hrCollectionsService's versions subcollection) ----

const importVersionsRef = (docId) => collection(db, COLLECTION, docId, 'versions');

const toIso = (ts) => {
  try {
    const d = typeof ts?.toDate === 'function' ? ts.toDate() : ts ? new Date(ts) : new Date();
    return Number.isNaN(d.getTime()) ? new Date().toISOString() : d.toISOString();
  } catch {
    return new Date().toISOString();
  }
};

/** Lazily seed the first version entry from the parent's flat fields (pre-history docs). */
async function seedInitialImportVersionIfNeeded(docId, parentData) {
  const snap = await getDocs(importVersionsRef(docId));
  if (!snap.empty) return;
  if (!parentData?.downloadURL) return;
  await addDoc(importVersionsRef(docId), {
    version: parentData.version || '1.0',
    fileName: parentData.fileName || '',
    size: parentData.size || 0,
    downloadURL: parentData.downloadURL,
    storagePath: parentData.storagePath || '',
    fileKind: getFileKindFromRow(parentData),
    uploadedByUid: parentData.createdBy || '',
    uploadedByName: parentData.authorName || 'Unknown',
    uploadedAt: toIso(parentData.updatedAt || parentData.createdAt),
    notes: 'Imported from prior upload',
  });
}

/** Keep the public bootcamp mirror pointing at the parent's current file. */
async function syncBootcampMirrorFile(bootcampImportId, { downloadURL, storagePath, fileName, fileKind }) {
  if (!bootcampImportId) return;
  try {
    await updateDoc(doc(db, BOOTCAMP_COLLECTION, bootcampImportId), {
      downloadURL,
      storagePath,
      fileName,
      fileKind,
    });
  } catch (e) {
    console.warn('bootcampImports file sync:', e);
  }
}

/**
 * Upload a new file version for an existing import: appends an immutable
 * version entry and updates the parent "latest" fields. Old files stay in
 * Storage — version entries reference them.
 */
export async function uploadTemplatesImportVersion({ docId, file, version, notes = '', user = {} }) {
  if (!file) throw new Error('Choose a file to upload.');
  if (!version || !version.trim()) throw new Error('A version number is required.');

  const mainRef = doc(db, COLLECTION, docId);
  const snap = await getDoc(mainRef);
  if (!snap.exists()) throw new Error('Import not found.');
  const data = snap.data();
  const tabId = data.tabId ?? data.importType;
  const fileKind = getFileKindForTab(tabId);

  assertImportFile(tabId, file.name);
  if (fileKind === 'json') {
    try {
      JSON.parse(await file.text());
    } catch (e) {
      if (e instanceof SyntaxError) throw new Error('Invalid JSON. Check the file and try again.');
      throw new Error('Could not read the file.');
    }
  }
  if (file.size > 15 * 1024 * 1024) throw new Error('File must be 15MB or smaller.');

  const safeName = sanitizeFileName(file.name);
  const storagePath = `templates-imports/${tabId}/${user.uid || 'crm'}_${Date.now()}_${safeName}`;
  const snapshot = await uploadBytes(ref(storage, storagePath), file, attachmentUploadMetadata(file.name));
  const downloadURL = await getDownloadURL(snapshot.ref);

  await seedInitialImportVersionIfNeeded(docId, data);

  await updateDoc(mainRef, {
    fileName: file.name,
    storagePath,
    downloadURL,
    size: file.size,
    fileKind,
    version: version.trim(),
    updatedAt: serverTimestamp(),
  });

  await addDoc(importVersionsRef(docId), {
    version: version.trim(),
    fileName: file.name,
    size: file.size,
    downloadURL,
    storagePath,
    fileKind,
    uploadedByUid: user.uid || '',
    uploadedByName: user.name || 'Unknown',
    uploadedAt: new Date().toISOString(),
    notes: notes.trim(),
  });

  await syncBootcampMirrorFile(data.bootcampImportId, {
    downloadURL,
    storagePath,
    fileName: file.name,
    fileKind,
  });

  return { docId, downloadURL, storagePath };
}

/** List an import's version history, newest first. */
export async function fetchImportVersionHistory(docId) {
  if (!docId) return [];
  const snap = await getDoc(doc(db, COLLECTION, docId));
  if (snap.exists()) {
    await seedInitialImportVersionIfNeeded(docId, snap.data());
  }
  const q = query(importVersionsRef(docId), orderBy('uploadedAt', 'desc'));
  const versionsSnap = await getDocs(q);
  return versionsSnap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

/**
 * Point the parent back at a prior version's file and append a new version
 * entry recording the rollback (history is never destroyed).
 */
export async function rollbackImportToVersion(docId, versionId, user = {}) {
  if (!docId || !versionId) throw new Error('docId and versionId are required');
  const versionSnap = await getDoc(doc(db, COLLECTION, docId, 'versions', versionId));
  if (!versionSnap.exists()) throw new Error('Version not found.');
  const v = versionSnap.data();

  const mainRef = doc(db, COLLECTION, docId);
  const mainSnap = await getDoc(mainRef);
  const bootcampImportId = mainSnap.exists() ? mainSnap.data()?.bootcampImportId : null;

  await updateDoc(mainRef, {
    fileName: v.fileName,
    storagePath: v.storagePath,
    downloadURL: v.downloadURL,
    size: v.size || 0,
    fileKind: v.fileKind || 'csv',
    version: v.version,
    updatedAt: serverTimestamp(),
  });

  const versionEntry = await addDoc(importVersionsRef(docId), {
    version: v.version,
    fileName: v.fileName,
    size: v.size || 0,
    downloadURL: v.downloadURL,
    storagePath: v.storagePath,
    fileKind: v.fileKind || 'csv',
    uploadedByUid: user.uid || '',
    uploadedByName: user.name || 'Unknown',
    uploadedAt: new Date().toISOString(),
    notes: `Rolled back to v${v.version}`,
  });

  await syncBootcampMirrorFile(bootcampImportId, {
    downloadURL: v.downloadURL,
    storagePath: v.storagePath,
    fileName: v.fileName,
    fileKind: v.fileKind || 'csv',
  });

  return { versionId: versionEntry.id };
}

/** For JTBootcamp (no auth). Requires Firestore rules allowing public read on `bootcampImports`. */
export async function listBootcampImportsPublic() {
  const snap = await getDocs(collection(db, BOOTCAMP_COLLECTION));
  const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
  rows.sort((a, b) => {
    const ta = a.createdAt?.toMillis?.() ?? a.createdAt?.seconds * 1000 ?? 0;
    const tb = b.createdAt?.toMillis?.() ?? b.createdAt?.seconds * 1000 ?? 0;
    return tb - ta;
  });
  return rows;
}

/** Public: whether the bootcamp name/email/phone gate is on (default true). */
export async function getBootcampUnlockEnabledPublic() {
  const ref = doc(db, BOOTCAMP_SETTINGS_COLLECTION, BOOTCAMP_SETTINGS_CONFIG_ID);
  const snap = await getDoc(ref);
  if (!snap.exists()) return true;
  const v = snap.data().unlockEnabled;
  return v !== false;
}

/** CRM: turn the public lead gate on or off. */
export async function setBootcampUnlockEnabled(enabled) {
  const ref = doc(db, BOOTCAMP_SETTINGS_COLLECTION, BOOTCAMP_SETTINGS_CONFIG_ID);
  await setDoc(
    ref,
    { unlockEnabled: !!enabled, updatedAt: serverTimestamp() },
    { merge: true }
  );
}

/** Public JT Bootcamp unlock form — unauthenticated create when rules allow. */
export async function submitBootcampUnlockLead({ name, email, phone }) {
  const nameTrim = String(name || '').trim();
  const emailTrim = String(email || '').trim();
  const phoneTrim = String(phone || '').trim();
  if (nameTrim.length < 2) throw new Error('Name is required.');
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(emailTrim)) {
    throw new Error('Valid email is required.');
  }
  const digits = phoneTrim.replace(/\D/g, '');
  if (digits.length < 10) throw new Error('Valid phone is required.');
  await addDoc(collection(db, UNLOCK_LEADS_COLLECTION), {
    name: nameTrim,
    email: emailTrim,
    phone: phoneTrim,
    source: JT_BOOTCAMP_UNLOCK_SOURCE,
    createdAt: Timestamp.now(),
  });
}

/** CRM: live unlock leads, newest first (internal auth + rules). Returns the unsubscribe. */
export function subscribeBootcampUnlockLeads(onRows, onError) {
  const q = query(
    collection(db, UNLOCK_LEADS_COLLECTION),
    orderBy('createdAt', 'desc')
  );
  return onSnapshot(
    q,
    (snap) => onRows(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );
}

export async function deleteBootcampUnlockLead(docId) {
  await deleteDoc(doc(db, UNLOCK_LEADS_COLLECTION, docId));
}

/**
 * CRM: live event lines, newest first. A line sits at its `at` time — unlock
 * submissions created after it render above it. Returns the unsubscribe.
 */
export function subscribeBootcampUnlockLines(onLines, onError) {
  const q = query(collection(db, UNLOCK_LINES_COLLECTION), orderBy('at', 'desc'));
  return onSnapshot(
    q,
    (snap) => onLines(snap.docs.map((d) => ({ id: d.id, ...d.data() }))),
    onError
  );
}

export async function addBootcampUnlockLine({ label, atMs, user }) {
  await addDoc(collection(db, UNLOCK_LINES_COLLECTION), {
    label: String(label || '').trim(),
    at: Timestamp.fromMillis(atMs),
    createdAt: serverTimestamp(),
    createdBy: user?.uid || null,
    createdByName: user?.name || null,
  });
}

export async function updateBootcampUnlockLine(lineId, { label, atMs }) {
  await updateDoc(doc(db, UNLOCK_LINES_COLLECTION, lineId), {
    label: String(label || '').trim(),
    at: Timestamp.fromMillis(atMs),
    updatedAt: serverTimestamp(),
  });
}

export async function deleteBootcampUnlockLine(lineId) {
  await deleteDoc(doc(db, UNLOCK_LINES_COLLECTION, lineId));
}
